<template>
  <div class="manager-company-show-ui">
    <VWrapper>
      <div class="manager-company-show-ui__head">
        <IconBtn
          variation-class-name="go-back"
          prepend-icon-class-name="fr fr-chevron-left"
          to="/manager/dashboard"
          flat
          :label="company?.name"
        >
          <template v-if="isLoading" #default>
            <ManagerCompanyTitleSkeleton />
          </template>
        </IconBtn>
      </div>
      <VSpinner v-if="isLoading" class="centered-block" />
      <VTabs v-else :tabs="tabs" toggle-manually swipeable class="manager-company-show">
        <template v-for="(_, index) in tabs" #[`btn-${index}`]="{ label }" :key="index">
          <span class="manager-company-show-ui__tab-label">{{ label }}</span>
        </template>
        <template #tab-0="{ active }">
          <keep-alive>
            <ManagerCompanyEdit
              v-if="active"
              :company-id="companyId"
              :payload="companyPayload"
              :loading="isLoading"
              :external-id="company?.external_id"
            />
          </keep-alive>
        </template>
        <template #tab-1="{ active }">
          <template v-if="companyId">
            <ManagerCompanyUsers v-if="active" :company-id="companyId" />
          </template>
          <VFallbackText v-else class="centered">Something went wrong</VFallbackText>
        </template>
        <template
          v-for="(currentPart, index) in filteredParts.slice(2)"
          #[`tab-${index+2}`]="{ active }"
          :key="currentPart.key"
        >
          <component
            :is="views[currentPart.key]"
            v-if="active && currentPart.excludeKeepAlive"
            :company-id="companyId"
            v-bind="getExtraProps(currentPart.key)"
          />
          <keep-alive v-else>
            <component
              :is="views[currentPart.key]"
              v-if="active"
              :company-id="companyId"
              v-bind="getExtraProps(currentPart.key)"
            />
          </keep-alive>
        </template>
      </VTabs>
    </VWrapper>
  </div>
</template>

<script lang="ts" setup>
  // Types
  import type { PropType, DefineComponent } from 'vue';
  import type { ITab } from '~/misc/v-tabs/types';
  import type { TManagerCompanyPart, TManagerCompanyViews } from '~/modules/manager/company/types';
  import type { TManagerCompany, TNewManagerCompany } from '~/modules/manager/types';
  import { EManagerCompanyView } from '~/modules/manager/company/types';

  // Config
  import { MANAGER_COMPANY_PARTS } from '~/modules/manager/company/config';

  // Services
  import { ManagerCompanyDataClient } from '~/modules/manager/services/manager-company-data-client';

  // Composables
  import { useNotifyResource } from '~/misc/resource/composables/use-notify-resource';

  // Components
  import VWrapper from '~/misc/v-wrapper/VWrapper.vue';
  import IconBtn from '~/misc/btn/icon/IconBtn.vue';
  import ManagerCompanyTitleSkeleton from '~/modules/manager/company/title-skeleton/ManagerCompanyTitleSkeleton.vue';
  import VTabs from '~/misc/v-tabs';
  import ManagerCompanyEdit from '~/modules/manager/company/edit/ManagerCompanyEdit.vue';
  import ManagerCompanyUsers from '~/modules/manager/company/users/ManagerCompanyUsers.vue';
  import ManagerCompanyPatches from '~/modules/manager/company/patches/ManagerCompanyPatches.vue';
  import ManagerCompanySources from '~/modules/manager/company/sources/ManagerCompanySources.vue';
  import ManagerCompanyFeatures from '~/modules/manager/company/features/ManagerCompanyFeatures.vue';
  import ManagerCompanyImport from '~/modules/manager/company/import/ManagerCompanyImport.vue';
  import VFallbackText from '~/misc/v-fallback-text/VFallbackText.vue';
  import ManagerCompanyStats from '~/modules/manager/company/stats/ManagerCompanyStats.vue';
  import ManagerCompanyExternalOffices from '~/modules/manager/company/external-offices/ManagerCompanyExternalOffices.vue';
  import ManagerCompanyMerge from '~/modules/manager/company/merge/ManagerCompanyMerge.vue';
  import ManagerCompanyMarketAppraisals from '~/modules/manager/company/market-appraisal/ManagerCompanyMarketAppraisals.vue';
  import ManagerCompanyOpportunityLookup from '~/modules/manager/company/opportunity-lookup/ManagerCompanyOpportunityLookup.vue';
  import ManagerCompanyGlobalFilters from '~/modules/manager/company/global-filters/ManagerCompanyGlobalFilters.vue';
  import VSpinner from '~/misc/v-spinner/VSpinner.vue';

  const props = defineProps({
    companyId: {
      type: Number as PropType<number | null>,
      default: null,
    },
  });

  const managerCompanyDataClient: ManagerCompanyDataClient = new ManagerCompanyDataClient();

  const { notifyFailure } = useNotifyResource();

  const company: Ref<TManagerCompany | null> = ref<TManagerCompany | null>(null);
  const isLoading: Ref<boolean> = ref<boolean>(false);

  const companyPayload: ComputedRef<TNewManagerCompany> = computed<TNewManagerCompany>(() => ({
    name: company.value?.name ?? '',
    aliases: company.value?.aliases ?? [],
    sync_archived: company.value?.sync_archived ?? true,
    sync_applicants: company.value?.sync_applicants ?? true,
    sync_landlords: company.value?.sync_landlords ?? true,
    excluded_marketing_consents: company.value?.excluded_marketing_consents ?? [],
    show_contact_info: company.value?.show_contact_info ?? true,
    allow_journal: company.value?.allow_journal ?? true,
    write_journal: company.value?.write_journal ?? true,
  }));

  const filteredParts: ComputedRef<TManagerCompanyPart[]> = computed(() =>
    MANAGER_COMPANY_PARTS.filter(
      (part: TManagerCompanyPart) => !part.requiresExternal || company.value?.is_external === true
    )
  );

  const tabs: ComputedRef<ITab[]> = computed<ITab[]>(() =>
    filteredParts.value.map((item: TManagerCompanyPart) => ({ label: item.label }))
  );

  const views: TManagerCompanyViews = {
    [EManagerCompanyView.AGENCY]: ManagerCompanyEdit as unknown as DefineComponent,
    [EManagerCompanyView.USERS]: ManagerCompanyUsers as unknown as DefineComponent,
    [EManagerCompanyView.AREAS]: ManagerCompanyPatches as unknown as DefineComponent,
    [EManagerCompanyView.SOURCES]: ManagerCompanySources as unknown as DefineComponent,
    [EManagerCompanyView.TOOLS]: ManagerCompanyFeatures as unknown as DefineComponent,
    [EManagerCompanyView.MARKET_APPRAISALS]:
      ManagerCompanyMarketAppraisals as unknown as DefineComponent,
    [EManagerCompanyView.UPLOAD_ADDRESSES]: ManagerCompanyImport as unknown as DefineComponent,
    [EManagerCompanyView.STATS]: ManagerCompanyStats as unknown as DefineComponent,
    [EManagerCompanyView.EXTERNAL_OFFICES]:
      ManagerCompanyExternalOffices as unknown as DefineComponent,
    [EManagerCompanyView.OPPORTUNITY_LOOKUP]:
      ManagerCompanyOpportunityLookup as unknown as DefineComponent,
    [EManagerCompanyView.GLOBAL_FILTERS]: ManagerCompanyGlobalFilters as unknown as DefineComponent,
    [EManagerCompanyView.MERGE]: ManagerCompanyMerge as unknown as DefineComponent,
  };

  const setCompany = (data: TManagerCompany) => {
    company.value = { ...data };
  };

  const setIsLoading = (state: boolean) => {
    isLoading.value = state;
  };

  const getExtraProps = (key: EManagerCompanyView) => {
    switch (key) {
      case EManagerCompanyView.MERGE:
      case EManagerCompanyView.AREAS:
        return { companyName: company.value?.name ?? null };
      case EManagerCompanyView.GLOBAL_FILTERS:
        return { isExternalCompany: company.value?.is_external ?? false };
      default:
        return {};
    }
  };

  const fetchCompany = async () => {
    if (props.companyId === null) {
      throw new Error('Company ID is not provided');
    }

    try {
      setIsLoading(true);

      const response: TManagerCompany = await managerCompanyDataClient.request(
        'show',
        props.companyId
      );

      setCompany(response);
    } catch (error) {
      const message: string = 'Something went wrong during fetching the company details';

      notifyFailure(message);

      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  };

  fetchCompany();
</script>
